import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import TransactionFloorRow, { txHeat } from "./TransactionFloorRow";

const SWIPE_THRESHOLD = 52;

export default function TransactionFloorIndex({
  transactions,
  selectedId,
  onSelect,
  desk,
  pagination,
  loading,
  onPageChange,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0 });
  const [slideDir, setSlideDir] = useState(null);

  const { page = 1, totalPages = 1, total = 0, limit = 15 } = pagination || {};
  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + transactions.length, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const maxAmount = useMemo(() => {
    if (!transactions.length) return 1;
    return Math.max(1, ...transactions.map((tx) => Math.abs(Number(tx.amount) || 0)));
  }, [transactions]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page]);

  useEffect(() => {
    if (!slideDir) return undefined;
    const timer = window.setTimeout(() => setSlideDir(null), 220);
    return () => window.clearTimeout(timer);
  }, [slideDir, page]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  const goPage = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setSlideDir(nextPage > page ? "next" : "prev");
    onPageChange(nextPage);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (totalPages <= 1) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goPage(page + 1);
    else goPage(page - 1);
  };

  return (
    <aside className="amf-index atx-index" aria-label="Transaction ledger">
      <div className="amf-index-head" aria-live="polite">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">Transaction ledger</span>
          <span className="amf-index-count">
            {total > 0 ? (
              <>
                <strong>
                  {startIndex + 1}–{endIndex}
                </strong>
                {" / "}
                <strong>{total}</strong>
              </>
            ) : (
              <strong>{transactions.length}</strong>
            )}
            {totalPages > 1 ? ` · p${page}` : ""}
            {desk === "needs_action" ? " · queue" : ""}
          </span>
        </div>
      </div>

      <div
        ref={listRef}
        className={`amf-index-list atx-index-list${slideDir ? ` atx-index-list-${slideDir}` : ""}${loading ? " atx-index-list-loading" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {loading && transactions.length === 0 ? (
          <div className="atx-index-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading page…</span>
          </div>
        ) : transactions.length === 0 ? (
          <div className="amf-index-empty">No transactions match current filters.</div>
        ) : (
          transactions.map((tx, idx) => {
            const id = tx._id;
            const heatPct = txHeat(tx, maxAmount);
            return (
              <div key={id} ref={id === selectedId ? selectedRef : null}>
                <TransactionFloorRow
                  tx={tx}
                  rank={startIndex + idx + 1}
                  selected={id === selectedId}
                  onSelect={() => onSelect(id)}
                  heatPct={heatPct}
                />
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 ? (
        <div className="atx-index-foot">
          <span className="atx-index-foot-meta">Swipe list or use arrows</span>
          <div className="atx-index-pager">
            <button
              type="button"
              className="atx-pager-btn"
              disabled={!canPrev || loading}
              onClick={() => goPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <span className="atx-pager-label">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="atx-pager-btn"
              disabled={!canNext || loading}
              onClick={() => goPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
